import PhotoSwipeLightbox from "./photoswipe-lightbox.esm.min.js";
import Photoswipe from "./photoswipe.esm.min.js";

class ZoomAction extends HTMLElement {
  constructor() {
    super();

    this.type = this.dataset.type || "no_zoom";
    this.zoomOption = this.dataset.zoomOption || "";
    this.lightbox = null;
    this.drift = null;
    this.position = 0;

    this.handleResizeListener = theme.helpers.throttle(this.init.bind(this));
  }

  connectedCallback() {
    window.addEventListener("resize", this.handleResizeListener, { passive: true });
    window.addEventListener("load", this.init.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResizeListener);
    window.removeEventListener("load", this.init.bind(this));
  }

  init() {
    if (theme.config.querySmall) {
      if (this.type !== "open_lightbox" && this.drift !== null) {
        this.drift.destroy();

        this.drift = null;
      }

      this.initLightBox();
    } else {
      if (this.type === "open_lightbox") {
        this.initLightBox();
      } else {
        if (this.lightbox !== null) {
          this.lightbox.destroy();

          this.lightbox = null;
        }

        this.initDrift();
      }
    }

    this.updateSwiper();
  }

  initLightBox() {
    if (this.lightbox !== null) {
      return;
    }

    const images = this.querySelectorAll("a.media-gallery__image");

    images.forEach((image) => {
      image.addEventListener("click", this.handleImageClick.bind(this));
    });
  }

  handleImageClick(evt) {
    evt.preventDefault();

    const target = evt.currentTarget;
    const mediaGallery = target.closest("media-gallery");
    const carousel = target.closest("slide-with-thumbs");
    let currentImageIndex = 0;

    if (target.getAttribute("data-image-position")) {
      this.position = target.getAttribute("data-image-position");
    } else if (mediaGallery.querySelector(".swiper-slide-active")) {
      this.position = mediaGallery.querySelector(".swiper-slide-active").getAttribute("data-position");
    } else if (mediaGallery.firstChild) {
      this.position = mediaGallery.firstChild?.getAttribute("data-position");
    }

    this.lightbox = new PhotoSwipeLightbox({
      gallery: this,
      children: "a",
      pswpModule: () => Photoswipe,
      counter: false,
      zoom: false,
      preloader: false,
      arrowPrev: false,
      arrowNext: false,
      close: false,
      loop: true,
      bgOpacity: 1
    });

    let plugin = this.lightbox.pswp;

    this.lightbox.on("uiRegister", () => {
      plugin = this.lightbox.pswp;

      if (!plugin) {
        return;
      }

      plugin.ui.registerElement({
        name: "bls--close",
        className: "lightbox__close button button-reset hover:scale[1.15]",
        isButton: true,
        tagName: "button",
        html: `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" class="transition" viewBox="0 0 12.82 12.28">
            <path d="M12.82 12.16 8.34 7.91M0 0l4.56 4.32" stroke="currentColor" style="stroke-dashoffset: 0; stroke-dasharray: 6.2283px, 6.3283px;"></path>
            <path d="M.34 12.28 12.54.22" stroke="currentColor" style="stroke-dashoffset: 0; stroke-dasharray: none;"></path>
          </svg>
        `,
        onClick: () => {
          plugin.close();
        },
      });

      plugin.ui.registerElement({
        name: "bottomBar",
        className: "pswp__bottom-bar",
        appendTo: "wrapper",
        onInit: (element, plugin) => {
          // Next button
          const nextButton = document.createElement("button");

          nextButton.setAttribute("type", "button");
          nextButton.className = "pswp__button pswp__button-next";

          nextButton.innerHTML =`
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 6L16 12L10 18" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          `;

          nextButton.addEventListener("click", () => {
            plugin.next();
          });

          // Previous button
          const previousButton = document.createElement("button");

          previousButton.setAttribute("type", "button");
          previousButton.className = "pswp__button pswp__button-prev";

          previousButton.innerHTML =`
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          `;

          previousButton.addEventListener("click", () => {
            plugin.prev();
          });

          // Counter
          const counter = document.createElement("span");

          counter.className = "pswp__counter";

          plugin.on("change", () => {
            counter.innerText = plugin.currIndex + 1 + plugin.options.indexIndicatorSep + plugin.getNumItems();
          });

          // Add to popup
          element.appendChild(nextButton);
          element.appendChild(counter);
          element.appendChild(previousButton);
        },
      });
    });

    this.lightbox.on("change", () => {
      if (!plugin) {
        return;
      }

      const currentIndex = plugin.currIndex;

      this.scrollToTargetImage(carousel, currentIndex);
    });

    this.lightbox.on("initialLayout", () => {
      document.dispatchEvent(new CustomEvent("hover-button:apply"));
    });

    this.lightbox.on("afterInit", () => {
      plugin = this.lightbox.pswp;
    });

    this.lightbox.on("closingAnimationEnd", () => {
      this.scrollToTargetImage(carousel, currentImageIndex);
    });

    this.lightbox.init();
    this.lightbox.loadAndOpen(this.position - 1);
  }

  scrollToTargetImage(carousel, position) {
    if (!Number.isInteger(position)) {
      return;
    }

    const targetImage = this.querySelector(`a.media-gallery__image[data-image-position='${position + 1}']`);

    if (carousel) {
      carousel.functionGoto(position, 500);
    } else if (targetImage) {
      targetImage.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }
  }

  initDrift() {
    if (this.drift !== null) {
      return;
    }

    const driftTriggers = this.querySelectorAll(".drift-trigger");

    driftTriggers.forEach((trigger) => {
      const inlineContainer = trigger.closest("[data-pane-container]");
      let inlinePane = this.zoomOption;
      let zoomFactor = 5;

      if (this.zoomOption === "inner-2") {
        inlinePane = (this.zoomOption === "inner-2");
        zoomFactor = 3;

        inlineContainer.addEventListener("click", (evt) => {
          evt.preventDefault();
        });
      }

      this.drift = new Drift(trigger, {
        inlinePane: inlinePane,
        zoomFactor: zoomFactor,
        paneContainer: inlineContainer,
        hoverBoundingBox: false
      });
    });
  }

  updateSwiper() {
    if (typeof Swiper !== "undefined") {
      const thumbnails = this.querySelector(".product-thumbnails-slider");

      if (thumbnails && thumbnails.swiper) {
        thumbnails.swiper.update();
      }
    }
  }
}

if (!window.customElements.get("zoom-action")) {
  customElements.define("zoom-action", ZoomAction);
}
